import {ILookup} from 'models/common/ILookup';

/** id (stringified) -> display name */
export type ILookupNameIndex = Record<string, string>;

/** Name indexes for selects that must submit both id and name. */
export interface IResourceMetadataLookupNames {
  imageSources: ILookupNameIndex;
  ethnicities: ILookupNameIndex;
  genders: ILookupNameIndex;
  publishedOutputTools: ILookupNameIndex;
  associatedResources: ILookupNameIndex;
  characterTraits: ILookupNameIndex;
  behavioralIndicators: ILookupNameIndex;
  accessibilityStatuses: ILookupNameIndex;
}

export const emptyResourceMetadataLookupNames = (): IResourceMetadataLookupNames => ({
  imageSources: {},
  ethnicities: {},
  genders: {},
  publishedOutputTools: {},
  associatedResources: {},
  characterTraits: {},
  behavioralIndicators: {},
  accessibilityStatuses: {},
});

type INamedEntity = {
  id?: number | string | null;
  contentItemId?: string | null;
  name?: string | null;
  displayText?: string | null;
  title?: string | null;
};

export const buildLookupNameIndex = (items: INamedEntity[] = []): ILookupNameIndex =>
  items.reduce<ILookupNameIndex>((acc, item) => {
    const id = item?.id ?? item?.contentItemId;
    if (id === undefined || id === null || id === '') {
      return acc;
    }

    // Prefer displayText for Associated Resources / resource list labels.
    acc[String(id)] = item.displayText ?? item.name ?? item.title ?? '';
    return acc;
  }, {});

const resolveName = (id: number | string, index?: ILookupNameIndex): string => index?.[String(id)] ?? '';

/** Pull a primitive id + display name out of a form value that may be an id, IOption, or ILookup. */
export const normalizeToStringLookup = (entry: unknown, nameIndex?: ILookupNameIndex): ILookup<string> | null => {
  if (entry === null || entry === undefined || entry === '') {
    return null;
  }

  if (typeof entry === 'object') {
    const obj = entry as Record<string, unknown>;

    // Autocomplete option accidentally stored in form state: {value, label}
    if ('value' in obj && obj.value !== null && obj.value !== undefined && typeof obj.value !== 'object') {
      const id = String(obj.value);
      const name = (typeof obj.label === 'string' && obj.label) || resolveName(id, nameIndex) || '';
      return {id, name};
    }

    // Lookup / API shape: {id, name} or {id, displayText}
    if ('id' in obj && obj.id !== null && obj.id !== undefined && typeof obj.id !== 'object') {
      const id = String(obj.id);
      const name =
        (typeof obj.name === 'string' && obj.name) ||
        (typeof obj.displayText === 'string' && obj.displayText) ||
        resolveName(id, nameIndex) ||
        '';
      return {id, name};
    }

    return null;
  }

  const id = String(entry);
  if (id === '[object Object]') {
    return null;
  }

  return {id, name: resolveName(id, nameIndex)};
};

export const toOptionalNumber = (value?: number | string | null): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/** true / false / 'true' / 'false' → boolean; anything else (including empty) → null. */
export const toOptionalBoolean = (value: unknown): boolean | null => {
  if (value === true || value === 'true') {
    return true;
  }

  if (value === false || value === 'false') {
    return false;
  }

  return null;
};

/** Extract a numeric id from a plain id, IOption `{value}`, or ILookup `{id}`. */
export const extractNumericId = (entry: unknown): number | undefined => {
  if (entry === null || entry === undefined || entry === '') {
    return undefined;
  }

  if (typeof entry === 'object') {
    const obj = entry as Record<string, unknown>;
    // Autocomplete option shape accidentally left in form state
    if ('value' in obj && typeof obj.value !== 'object') {
      return toOptionalNumber(obj.value as number | string | null);
    }
    if ('id' in obj && typeof obj.id !== 'object') {
      return toOptionalNumber(obj.id as number | string | null);
    }
    return undefined;
  }

  return toOptionalNumber(entry as number | string);
};

export const toNumberIds = (entries: unknown[] = []): number[] =>
  (entries ?? []).map(extractNumericId).filter((id): id is number => id !== undefined);

export const toNumberLookup = (
  id?: number | string | null,
  nameIndex?: ILookupNameIndex
): ILookup<number> | undefined => {
  const parsed = toOptionalNumber(id);
  if (parsed === undefined) {
    return undefined;
  }

  return {id: parsed, name: resolveName(parsed, nameIndex)};
};

export const toNumberLookups = (entries: unknown[] = [], nameIndex?: ILookupNameIndex): ILookup<number>[] =>
  (entries ?? [])
    .map((entry) => {
      const id = extractNumericId(entry);
      if (id === undefined) {
        return undefined;
      }

      let name = resolveName(id, nameIndex);
      if (!name && entry !== null && typeof entry === 'object') {
        const obj = entry as {name?: string; label?: string; displayText?: string};
        name = obj.name || obj.label || obj.displayText || '';
      }

      return {id, name};
    })
    .filter((lookup): lookup is ILookup<number> => !!lookup);

export const toStringLookups = (entries: unknown[] = [], nameIndex?: ILookupNameIndex): ILookup<string>[] =>
  (entries ?? [])
    .map((entry) => normalizeToStringLookup(entry, nameIndex))
    .filter((lookup): lookup is ILookup<string> => !!lookup);
